import type { ChannelPoint } from "@energyplatform/client";

// Placing a whole device: one point per measured channel of the device and of the other channels of the same
// vendor device (a SUPLA meter's total and phases, every meter of a Lavva device, every quantity of a multisensor).
// Pure, so the rules are testable. What a channel measures comes from the host (ChannelPoint, by capability).

export interface Member {
  deviceId: string;
  name: string;
  /** Channels that have reported values ("main", "phase1"…); a device that reported nothing counts with "main". */
  channels: string[];
  /** What kind of point each channel of the device can be. */
  kinds: ChannelPoint[];
}

export interface PlannedPoint {
  name: string;
  medium: string;
  role: string;
  /** Condition points measure one quantity; their medium names it (temperature, humidity…). */
  kind: ChannelPoint["kind"];
  device: { deviceId: string; channel: string; invert: boolean };
}

const CHANNEL_ORDER = (key: string) => (key === "main" ? "" : key);
const OTHER: Omit<ChannelPoint, "channel"> = {
  kind: "other",
  medium: "el",
  media: [],
  roles: [],
};

/**
 * Every channel becomes a point of its own kind. A condition (temperature, pressure…) is an environment point of its
 * quantity. Among meters the first one gets the chosen name, role and medium; the other meter channels measure part
 * of it (phases, further meters of the device) and become sub-meters, never added to a balance (doc 12, section 7).
 */
export function wholeDevicePoints(
  members: readonly Member[],
  chosen: { name: string; role: string; medium: string },
  channelLabel: (key: string) => string,
): PlannedPoint[] {
  const result: PlannedPoint[] = [];
  let primaryTaken = false;
  members.forEach((m, index) => {
    const channels = [
      ...new Set(m.channels.length > 0 ? m.channels : ["main"]),
    ].sort((a, b) => CHANNEL_ORDER(a).localeCompare(CHANNEL_ORDER(b)));
    const base = index === 0 ? chosen.name : m.name;
    for (const key of channels) {
      const kind = m.kinds.find((k) => k.channel === key) ?? {
        channel: key,
        ...OTHER,
      };
      const name =
        channels.length > 1 ? `${base} · ${channelLabel(key)}` : base;
      const device = { deviceId: m.deviceId, channel: key, invert: false };
      if (kind.kind === "condition") {
        result.push({
          name,
          medium: kind.medium,
          role: "environment",
          kind: kind.kind,
          device,
        });
        continue;
      }
      const primary = !primaryTaken;
      primaryTaken = true;
      const medium =
        kind.media.length === 0 || kind.media.includes(chosen.medium)
          ? chosen.medium
          : kind.medium;
      const role =
        kind.roles.length === 0 || kind.roles.includes(chosen.role)
          ? chosen.role
          : kind.roles[0]!;
      result.push({
        name,
        medium,
        role: primary ? role : "sub",
        kind: kind.kind,
        device,
      });
    }
  });
  return result;
}
